import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useStripes, useOkapiKy } from '@folio/stripes/core';
import { useIntl } from 'react-intl';
import useRecordLocks from './RecordLocks/useRecordLocks';

const LOCK_TIMEOUT_MINUTES = 5;

function useRecordLock(recordId, onInactivityDetected) {
  const { user, okapi } = useStripes();
  const ky = useOkapiKy();
  const intl = useIntl();
  const { recordLocks, configId, refetch } = useRecordLocks();

  // Force refetch when recordId changes to get fresh lock data
  useEffect(() => {
    if (recordId) {
      console.log('🔄 [useRecordLock] Refreshing lock data for recordId:', recordId);
      refetch();
    }
  }, [recordId, refetch]);


  // Save locks to config API
  const saveLocks = useCallback(
    async (locks) => {
      console.log('💾 [saveLocks] Attempting to save:', { locks, configId });
      try {
        if (configId) {
          console.log('💾 [saveLocks] Updating existing config');
          await ky.put(`inventory-templates-configs/${configId}`, {
            json: {
              id: configId,
              configName: 'recordlocks',
              configCode: 'recordlocks',
              configValue: locks,
            },
          });
        } else {
          console.log('💾 [saveLocks] Creating new config');
          await ky.post('inventory-templates-configs', {
            json: {
              configName: 'recordlocks',
              configCode: 'recordlocks',
              configValue: locks,
            },
          });
        }
        console.log('💾 [saveLocks] API call successful, refetching...');
        await refetch();
        console.log('💾 [saveLocks] Refetch completed');
      } catch (error) {
        console.error('❌ [saveLocks] Failed to save locks:', error);
        console.error('❌ [saveLocks] Error details:', {
          message: error.message,
          status: error.status,
          statusText: error.statusText,
          url: error.url
        });
        throw error;
      }
    },
    [configId, ky, refetch],
  );

  // Get lock info for this record
  const lockInfo = useMemo(() => {
    // Only log if we have actual data to prevent spam
    if (Object.keys(recordLocks).length > 0) {
      console.log('🔍 [useRecordLock] Checking lock for recordId:', recordId);
      console.log('🔍 [useRecordLock] Current recordLocks:', recordLocks);
    }

    if (!recordId || !recordLocks[recordId]) {
      if (Object.keys(recordLocks).length > 0) {
        console.log('🔍 [useRecordLock] No lock found for this record');
      }
      return null;
    }

    const lock = recordLocks[recordId];
    const now = Date.now();
    const timeoutMs = LOCK_TIMEOUT_MINUTES * 60 * 1000;

    // Check if lock is expired
    if (now - lock.timestamp > timeoutMs) {
      console.log('⏰ [useRecordLock] Lock expired, removing it');
      return null;
    }

    const isOwnLock = lock.userId === user?.user?.id;
    const duration = Math.floor((now - lock.timestamp) / (60 * 1000));

    console.log('🔍 [useRecordLock] Lock found:', {
      lockUserId: lock.userId,
      currentUserId: user?.user?.id,
      isOwnLock,
      duration,
      lockAge: now - lock.timestamp
    });

    return {
      userId: lock.userId,
      userDisplayName: lock.userDisplayName,
      timestamp: lock.timestamp,
      isOwnLock,
      duration: duration > 0 ? duration : 1,
    };
  }, [recordLocks, recordId, user?.user?.id]);

  // Derived states
  const isLocked = Boolean(lockInfo);
  const isOwnLock = Boolean(lockInfo?.isOwnLock);

  // Lock a record
  const lockRecord = useCallback(
    async () => {
      console.log('🔐 [lockRecord] Attempting to lock record:', recordId);
      console.log('🔐 [lockRecord] Current lock state:', { isLocked, isOwnLock, lockInfo, configId });

      if (!recordId) return { success: false, error: intl.formatMessage({ id: 'ui-plugin-inventory-templates.recordLock.error.noRecordId', defaultMessage: 'No record ID provided' }) };

      // Wait for config to be loaded before attempting to lock
      if (configId === undefined) {
        console.log('⏳ [lockRecord] Config not loaded yet, waiting...');
        return { success: false, error: 'Config not loaded yet' };
      }

      try {
        if (isOwnLock) {
          console.log('✅ [lockRecord] User already owns this lock');
          return { success: true, isNewLock: false };
        }

        if (isLocked && !isOwnLock) {
          console.log('❌ [lockRecord] Record locked by another user:', lockInfo.userDisplayName);
          return {
            success: false,
            conflict: lockInfo,
            message: intl.formatMessage({ id: 'ui-plugin-inventory-templates.recordLock.error.lockedByUser', defaultMessage: 'Record is locked by {user}' }, { user: lockInfo.userDisplayName }),
          };
        }

        const newLock = {
          userId: user?.user?.id || 'anonymous',
          userDisplayName: user?.user?.personal?.firstName
            ? `${user.user.personal.firstName} ${user.user.personal.lastName || ''}`.trim()
            : user?.user?.username || intl.formatMessage({ id: 'ui-plugin-inventory-templates.recordLock.unknownUser', defaultMessage: 'Unknown user' }),
          timestamp: Date.now(),
        };

        const updatedLocks = {
          ...recordLocks,
          [recordId]: newLock,
        };

        console.log('💾 [lockRecord] Creating new lock:', newLock);
        await saveLocks(updatedLocks);
        console.log('✅ [lockRecord] Lock created successfully');
        return { success: true, isNewLock: true, lock: newLock };
      } catch (error) {
        console.error('❌ [lockRecord] Failed to lock record:', error);
        return {
          success: false,
          error: error.message || intl.formatMessage({ id: 'ui-plugin-inventory-templates.recordLock.error.lockFailed', defaultMessage: 'Failed to lock record' }),
        };
      }
    },
    [recordId, isOwnLock, isLocked, lockInfo, recordLocks, saveLocks, user, okapi, intl, configId],
  );

  // Unlock a record
  const unlockRecord = useCallback(
    async () => {
      console.log('🔓 [unlockRecord] Attempting to unlock record:', recordId);
      if (!recordId) return { success: false, message: 'No record ID provided' };

      try {
        if (!isOwnLock) {
          console.log('❌ [unlockRecord] Cannot unlock - not lock owner');
          return { success: false, message: intl.formatMessage({ id: 'ui-plugin-inventory-templates.recordLock.error.cannotUnlockOther', defaultMessage: 'Cannot unlock another user\'s lock' }) };
        }

        const updatedLocks = { ...recordLocks };
        delete updatedLocks[recordId];
        console.log('💾 [unlockRecord] Removing lock from database');
        await saveLocks(updatedLocks);
        console.log('✅ [unlockRecord] Lock removed successfully');

        return { success: true };
      } catch (error) {
        console.error('❌ [unlockRecord] Failed to unlock record:', error);
        return {
          success: false,
          error: error.message || intl.formatMessage({ id: 'ui-plugin-inventory-templates.recordLock.error.unlockFailed', defaultMessage: 'Failed to unlock record' }),
        };
      }
    },
    [recordId, isOwnLock, recordLocks, saveLocks, intl],
  );

  // Track user activity locally without API calls
  const lastActivityTime = useRef(Date.now());
  const updateActivity = useCallback(() => {
    if (!recordId || !isOwnLock) return;

    // Update activity time locally only - no API calls
    lastActivityTime.current = Date.now();
    console.log('⏱️ [updateActivity] Activity tracked locally');
  }, [recordId, isOwnLock]);

  // Check for inactivity and trigger warning modal
  const checkInactivity = useCallback(async () => {
    if (!isOwnLock || !recordId) return null;

    const now = Date.now();
    const timeoutMs = LOCK_TIMEOUT_MINUTES * 60 * 1000;

    // Check if user has been inactive for too long
    if (now - lastActivityTime.current > timeoutMs) {
      console.log('⏰ [checkInactivity] User inactive for too long, triggering warning modal');

      // Remove the lock first
      try {
        await unlockRecord();
        return { shouldShowInactivityModal: true };
      } catch (error) {
        console.error('Failed to remove inactive lock:', error);
        return { shouldShowInactivityModal: true, error: error.message };
      }
    }

    return null;
  }, [isOwnLock, recordId, unlockRecord]);

  useEffect(() => {
    if (!isOwnLock || !recordId) return;

    // Check every 30 seconds - but only for lock owners
    const interval = setInterval(async () => {
      const result = await checkInactivity();
      if (result?.shouldShowInactivityModal && onInactivityDetected) {
        console.log('⚠️ [useRecordLock] Inactivity detected, triggering callback');
        onInactivityDetected(result);
      }
    }, 30 * 1000);

    return () => clearInterval(interval);
  }, [isOwnLock, recordId, checkInactivity, onInactivityDetected]);

  // Function to check if session can be extended
  const checkSessionExtension = useCallback(async (currentInstance) => {
    console.log('🔍 [checkSessionExtension] Checking if session can be extended');

    try {
      // 1. Check for existing locks by other users
      await refetch(); // Get latest lock data
      const currentLocks = recordLocks;
      const existingLock = currentLocks[recordId];

      if (existingLock && existingLock.userId !== user?.user?.id) {
        console.log('❌ [checkSessionExtension] Record locked by another user');
        return {
          success: false,
          error: `لا يمكن التمديد - التسجيلة مستخدمة من قبل ${existingLock.userDisplayName}`,
          hasVersionConflict: false,
        };
      }

      // 2. Check optimistic locking (version conflict)
      if (currentInstance) {
        try {
          // Fetch current version from server
          const response = await ky.get(`inventory/instances/${recordId}`).json();
          const serverVersion = response._version || response.metadata?.updatedDate;
          const clientVersion = currentInstance._version || currentInstance.metadata?.updatedDate;

          if (serverVersion !== clientVersion) {
            console.log('❌ [checkSessionExtension] Version conflict detected');
            return {
              success: false,
              error: 'لا يمكن التمديد - التسجيلة ليست آخر إصدار',
              hasVersionConflict: true,
            };
          }
        } catch (versionError) {
          console.error('Failed to check version:', versionError);
          return {
            success: false,
            error: 'حدث خطأ أثناء التحقق من إصدار التسجيلة',
            hasVersionConflict: false,
          };
        }
      }

      // 3. Try to restore the lock
      const lockResult = await lockRecord();
      if (lockResult.success) {
        console.log('✅ [checkSessionExtension] Session extended successfully');
        return { success: true };
      } else {
        return {
          success: false,
          error: lockResult.error || 'فشل في استعادة القفل',
          hasVersionConflict: false,
        };
      }
    } catch (error) {
      console.error('❌ [checkSessionExtension] Check failed:', error);
      return {
        success: false,
        error: 'حدث خطأ أثناء التحقق من حالة التسجيلة',
        hasVersionConflict: false,
      };
    }
  }, [recordId, recordLocks, refetch, user?.user?.id, ky, lockRecord]);

  return {
    // States
    isLocked,
    isOwnLock,
    lockInfo,

    // Actions
    lockRecord,
    unlockRecord,
    updateActivity,
    checkSessionExtension,
  };
}

export default useRecordLock;
